import React from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const FieldRow: React.FC<{ label: string; value: string }> = ({
  label,
  value,
}) => (
  <div
    style={{
      display: 'flex',
      gap: '8px',
      padding: '4px 0',
      fontSize: '14px',
      lineHeight: '1.4',
    }}>
    <span
      style={{
        minWidth: '140px',
        fontWeight: 600,
        color: '#6b7280',
        flexShrink: 0,
      }}>
      {label}
    </span>
    <span style={{ color: '#111827', wordBreak: 'break-word' }}>{value}</span>
  </div>
);

const SYNONYM_PREVIEW_COUNT = 5;

/**
 * Renders synonyms as a vertical list — first 5 shown, "+N more" reveals the rest
 * with a "Show less" button to collapse back. Long IUPAC names wrap cleanly.
 */
const SynonymsList: React.FC<{ synonyms: string[] }> = ({ synonyms }) => {
  const [expanded, setExpanded] = React.useState(false);

  if (synonyms.length === 0) {
    return <FieldRow label="Synonyms" value="—" />;
  }

  const collapsible = synonyms.length > SYNONYM_PREVIEW_COUNT;
  const visible =
    expanded || !collapsible
      ? synonyms
      : synonyms.slice(0, SYNONYM_PREVIEW_COUNT);
  const hiddenCount = synonyms.length - SYNONYM_PREVIEW_COUNT;

  const toggleStyle: React.CSSProperties = {
    marginTop: '4px',
    fontSize: '12px',
    fontWeight: 600,
    color: '#1e40af',
    background: 'none',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
  };

  return (
    <div
      style={{
        display: 'flex',
        gap: '8px',
        padding: '4px 0',
        fontSize: '14px',
        lineHeight: '1.4',
      }}>
      <span
        style={{
          minWidth: '140px',
          fontWeight: 600,
          color: '#6b7280',
          flexShrink: 0,
        }}>
        Synonyms
      </span>
      <div style={{ flex: 1 }}>
        {visible.map(s => (
          <div
            key={s}
            style={{
              color: '#111827',
              wordBreak: 'break-word',
              paddingBottom: '2px',
            }}>
            {s}
          </div>
        ))}
        {collapsible && !expanded && (
          <button onClick={() => setExpanded(true)} style={toggleStyle}>
            +{hiddenCount} more
          </button>
        )}
        {collapsible && expanded && (
          <button onClick={() => setExpanded(false)} style={toggleStyle}>
            Show less
          </button>
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// CASRegistryPanel
// ---------------------------------------------------------------------------

interface CASRegistryPanelProps {
  isCasVerified: boolean;
  casNumber?: string | null;
  synonyms?: string[];
  molecularFormula?: string | null;
  molecularMass?: string | null;
  inchi?: string | null;
  inchiKey?: string | null;
  smile?: string | null;
  canonicalSmile?: string | null;
  replacedRns?: string[];
  uri?: string | null;
}

/**
 * Read-only CAS Registry section for the Product modal.
 * Renders nothing when is_cas_verified is false.
 * CAS Registry is the authoritative source — data shown here is not editable.
 */
const CASRegistryPanel: React.FC<CASRegistryPanelProps> = ({
  isCasVerified,
  casNumber,
  synonyms,
  molecularFormula,
  molecularMass,
  inchi,
  inchiKey,
  smile,
  canonicalSmile,
  replacedRns,
  uri,
}) => {
  const [secondaryExpanded, setSecondaryExpanded] = React.useState(false);

  if (!isCasVerified) return null;

  const synonymList = synonyms ?? [];

  const replacedRnsValue =
    replacedRns && replacedRns.length > 0 ? replacedRns.join(', ') : '—';

  const hasSecondary = !!(
    molecularFormula ||
    molecularMass ||
    inchi ||
    inchiKey ||
    smile ||
    canonicalSmile ||
    uri ||
    (replacedRns && replacedRns.length > 0)
  );

  return (
    <div
      style={{
        margin: '12px 0 8px 0',
        border: '1px solid #bfdbfe',
        borderRadius: '6px',
        overflow: 'hidden',
        backgroundColor: '#eff6ff',
      }}>
      {/* Panel header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px 12px',
          backgroundColor: '#dbeafe',
          borderBottom: '1px solid #bfdbfe',
        }}>
        <span
          style={{
            fontSize: '11px',
            fontWeight: 700,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: '#1e40af',
            backgroundColor: '#93c5fd',
            padding: '2px 6px',
            borderRadius: '4px',
          }}>
          CAS
        </span>
        <span
          style={{
            fontSize: '13px',
            fontWeight: 600,
            color: '#1d4ed8',
          }}>
          CAS Registry
        </span>
      </div>

      {/* Always-visible fields */}
      <div style={{ padding: '8px 12px 4px 12px' }}>
        {casNumber && <FieldRow label="CAS Number" value={casNumber} />}
        <SynonymsList synonyms={synonymList} />
      </div>

      {/* More details accordion — formula, mass, structural identifiers */}
      {hasSecondary && (
        <Accordion
          expanded={secondaryExpanded}
          onChange={() => setSecondaryExpanded(prev => !prev)}
          slotProps={{ transition: { unmountOnExit: false } }}
          disableGutters
          elevation={0}
          sx={{ '&:before': { display: 'none' } }}
          style={{ backgroundColor: 'transparent' }}>
          <AccordionSummary
            expandIcon={
              <ExpandMoreIcon style={{ color: '#1e40af', fontSize: '18px' }} />
            }
            style={{
              minHeight: '32px',
              padding: '0 12px',
              fontSize: '12px',
              fontWeight: 600,
              color: '#1e40af',
              backgroundColor: 'transparent',
            }}>
            More details
          </AccordionSummary>
          <AccordionDetails
            style={{
              padding: '0 12px 8px 12px',
              backgroundColor: 'transparent',
            }}>
            {molecularFormula && (
              <FieldRow label="Molecular Formula" value={molecularFormula} />
            )}
            {molecularMass && (
              <FieldRow label="Molecular Mass" value={molecularMass} />
            )}
            {inchi && <FieldRow label="InChI" value={inchi} />}
            {inchiKey && <FieldRow label="InChI Key" value={inchiKey} />}
            {smile && <FieldRow label="SMILES" value={smile} />}
            {canonicalSmile && (
              <FieldRow label="Canonical SMILES" value={canonicalSmile} />
            )}
            {replacedRns && replacedRns.length > 0 && (
              <FieldRow label="Replaced RNs" value={replacedRnsValue} />
            )}
            {uri && <FieldRow label="URI" value={uri} />}
          </AccordionDetails>
        </Accordion>
      )}
    </div>
  );
};

export default CASRegistryPanel;
